import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getMyStores } from "@/lib/api/stores-api";
import type { Store as ApiStore } from "@/lib/api/stores-api";
import type { ApiStoreDTO } from "@/lib/api/auth-api";
import type { StoreTier } from "@/lib/billing/tier";

// ============================================================
// Types
// ============================================================

export interface Store {
  id: string;
  name: string;
  slug?: string;
  tier: StoreTier;
  /** Lihat `plan_name` di `stores-api.ts`. Tidak ada = jangan tampilkan apa pun. */
  planName?: string;
  category?: string;
  description?: string;
  companyName?: string;
  status: "active" | "suspended" | "trial";
  trialEndsAt: string | null;
  teamCount: number;
  maxTeam: number;
  balance: number;
  monthlyBudget: number | null;
  aiSystemPrompt?: string;
  createdAt: string;
  // Onboarding fields
  onboarding_status?: string;
  onboarding_progress?: Record<string, boolean>;
  onboarding_skipped?: string[];
  onboarding_completed_at?: string;
}

interface StoreState {
  stores: Store[];
  activeStoreId: string;
  isGlobalView: boolean;
  setActiveStore: (storeId: string) => void;
  toggleGlobalView: () => void;
  setGlobalView: (value: boolean) => void;
  getActiveStore: () => Store | undefined;
  setStores: (stores: Store[]) => void;
  loadStoresFromAPI: () => Promise<void>;
  setStoresFromApiResponse: (apiStores: ApiStore[] | ApiStoreDTO[]) => void;
}

// ============================================================
// Mock Stores
// ============================================================

export const MOCK_STORES: Store[] = [
  {
    id: "store-1",
    name: "Toko Utama",
    tier: "business",
    status: "active",
    trialEndsAt: null,
    teamCount: 8,
    maxTeam: 15,
    balance: 4500000,
    monthlyBudget: 5000000,
    createdAt: "2025-08-15T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
  {
    id: "store-2",
    name: "Toko Cabang Jakarta",
    tier: "starter",
    status: "active",
    trialEndsAt: null,
    teamCount: 3,
    maxTeam: 3,
    balance: 1200000,
    monthlyBudget: 2000000,
    createdAt: "2025-10-01T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
  {
    id: "store-3",
    name: "Toko Cabang Surabaya",
    tier: "starter",
    status: "trial",
    trialEndsAt: "2026-03-01T00:00:00Z",
    teamCount: 2,
    maxTeam: 3,
    balance: 500000,
    monthlyBudget: null,
    createdAt: "2026-02-01T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
];

// ============================================================
// Store
// ============================================================

function mapApiStoreToStore(s: ApiStore): Store {
  return {
    id: s.id,
    name: s.name,
    slug: s.slug,
    tier: s.tier as StoreTier,
    planName: s.plan_name,
    category: s.category,
    description: s.description,
    companyName: s.company_name,
    status: s.status as "active" | "suspended" | "trial",
    trialEndsAt: s.trial_ends_at || null,
    teamCount: s.team_count,
    maxTeam: s.max_team,
    balance: s.balance,
    monthlyBudget: s.monthly_budget ?? null,
    aiSystemPrompt: s.ai_system_prompt,
    createdAt: s.created_at,
    onboarding_status: s.onboarding_status,
    onboarding_progress: s.onboarding_progress ?? {},
    onboarding_skipped: s.onboarding_skipped ?? [],
    onboarding_completed_at: s.onboarding_completed_at,
  };
}

function mapApiStoreDTOToStore(s: ApiStoreDTO): Store {
  return {
    id: s.id,
    name: s.name,
    slug: s.slug,
    tier: s.tier as StoreTier,
    planName: s.plan_name,
    category: s.category,
    description: s.description,
    companyName: undefined,
    status: s.status as "active" | "suspended" | "trial",
    trialEndsAt: s.trial_ends_at || null,
    teamCount: s.team_count,
    maxTeam: s.max_team,
    balance: s.balance,
    monthlyBudget: s.monthly_budget ?? null,
    aiSystemPrompt: s.ai_system_prompt,
    createdAt: s.created_at,
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  };
}

function isApiStore(s: ApiStore | ApiStoreDTO): s is ApiStore {
  return "onboarding_status" in s;
}

export const useStoreStore = create<StoreState>()(
  persist(
    (set, get) => ({
      stores: MOCK_STORES,
      activeStoreId: "store-1",
      isGlobalView: false,

      setActiveStore: (storeId: string) => {
        set({ activeStoreId: storeId, isGlobalView: false });
      },

      toggleGlobalView: () => {
        set((state) => ({ isGlobalView: !state.isGlobalView }));
      },

      setGlobalView: (value: boolean) => {
        set({ isGlobalView: value });
      },

      getActiveStore: () => {
        const state = get();
        return state.stores.find((s) => s.id === state.activeStoreId);
      },

      setStores: (stores: Store[]) => {
        set({ stores });
      },

      loadStoresFromAPI: async () => {
        try {
          const apiStores = await getMyStores();
          if (apiStores.length > 0) {
            const mapped = apiStores.map(mapApiStoreToStore);
            const currentActiveId = get().activeStoreId;
            const activeExists = mapped.some((s) => s.id === currentActiveId);
            set({
              stores: mapped,
              activeStoreId: activeExists ? currentActiveId : mapped[0].id,
            });
            console.log("[Store] Loaded", mapped.length, "stores from API");
          }
        } catch (err) {
          console.warn("[Store] Failed to load stores from API, keeping current:", err);
        }
      },

      setStoresFromApiResponse: (apiStores: ApiStore[] | ApiStoreDTO[]) => {
        if (apiStores.length > 0) {
          const mapped = apiStores.map((s) => (isApiStore(s) ? mapApiStoreToStore(s) : mapApiStoreDTOToStore(s)));
          // QC-F14: checkAuth() memanggil ini di tiap pemuatan halaman (bukan
          // hanya login), jadi pilihan store pengguna harus bertahan kalau
          // masih sah — bukan disapu ke mapped[0] setiap kali.
          const currentActiveId = get().activeStoreId;
          const activeExists = mapped.some((s) => s.id === currentActiveId);
          set({
            stores: mapped,
            activeStoreId: activeExists ? currentActiveId : mapped[0].id,
          });
          console.log("[Store] Set", mapped.length, "stores from API response");
        }
      },
    }),
    {
      name: "kustomer-store",
      partialize: (state) => ({
        stores: state.stores,
        activeStoreId: state.activeStoreId,
        isGlobalView: state.isGlobalView,
      }),
    }
  )
);
